import { useState } from 'react';

import { keepPreviousData, useMutation } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import {
  EditIcon,
  EyeIcon,
  FilterIcon,
  PlusIcon,
  UploadIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import DialogExportRecords from '../../../../components/pages/backoffice/dialogs/export-records.jsx';
import Error from '../../../../components/feedback/error.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../../components/table/data-table-footer.jsx';
import { DataTableToolbar } from '../../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import { DebouncedInput } from '../../../../components/ui/input-debounced.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { useTable } from '../../../../hooks/use-table.jsx';
import { TABLE_FILTER_LIKE } from '../../../../utils/constants.js';
import { getExportFile } from '../../../../utils/files.js';
import { filterIgnoreAccents } from '../../../../utils/filters.js';

const TABLE_COLUMNS = [
  {
    id: 'select',
    header: ({ table }) => (
      <div className="pr-2">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Selecionar tudo"
          className="translate-y-[2px]"
        />
      </div>
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Selecionar linha"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Nome',
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
    cell: ({ row }) => {
      return (
        <Link to={row.original.ROWID}>
          <div className="underline lg:w-64">{row.getValue('name')}</div>
        </Link>
      );
    },
    enableSorting: true,
    enableHiding: false,
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Nº de Identificação',
    accessorKey: 'identificationDoc',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nº de Identificação" />
    ),
    cell: ({ row }) => <div>{row.getValue('identificationDoc')}</div>,
    enableSorting: true,
    enableHiding: true,
  },
  {
    title: 'Loja',
    accessorKey: 'shop',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
    cell: ({ row }) => {
      return <div>{row.getValue('shop')}</div>;
    },
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex flex-row space-x-2">
        <Link to={row.original.ROWID}>
          <Button
            variant="outline"
            className="h-auto p-2"
            aria-label="Ver registo"
          >
            <EyeIcon />
          </Button>
        </Link>
        <Link to={`${row.original.ROWID}/edit`}>
          <Button
            variant="outline"
            className="h-auto p-2"
            aria-label="Editar registo"
          >
            <EditIcon />
          </Button>
        </Link>
      </div>
    ),
  },
];

export function EligibilityWatchlistList() {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const {
    table,
    query: queryEligibilityWatchlist,
    filters,
    selectColumnFilter,
    setSelectColumnFilter,
  } = useTable(
    TABLE_COLUMNS,
    {
      queryKey: ['eligibility-watchlist'],
      queryFn: async () => {
        const { data: eligibilityWatchlist } = await msCatalystApi.get(
          `/eligibility-watchlist/paginate?${filters}`,
        );
        return eligibilityWatchlist.data;
      },
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            ...item,
            createdTime: item.CREATEDTIME,
          })),
        };
      },
    },
    {
      selectColumnFilter: 'name',
      sorting: [
        {
          id: 'createdTime',
          desc: true,
        },
      ],
    },
  );

  const mutationExportData = useMutation({
    mutationFn: async (fileType) => {
      try {
        return getExportFile(
          '/eligibility-watchlist/export',
          'funcionarios-internos-lojas',
          {
            fileType,
          },
        );
      } catch (error) {
        throw error;
      }
    },
    onError: () => {
      toast.error('Erro ao exportar Funcionários Internos / Lojas', {
        description: 'Tente novamente mais tarde',
      });
    },
    onSuccess: () => {
      toast.success('Exportação realizada com sucesso', {
        description:
          'Foi automaticamente realizado o download do ficheiro .csv',
      });
    },
    onSettled: () => {
      setExportDialogOpen(false);
    },
  });

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Funcionários Internos / Lojas (Watchlist)</Heading>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          <DialogExportRecords
            exportDialogOpen={exportDialogOpen}
            setExportDialogOpen={setExportDialogOpen}
            action={mutationExportData.mutate}
            description="O processo de exportação abrange toda a informação presente dos Funcionários Internos / Lojas na base de
          dados."
          />
          <Button asChild>
            <Link to="import">
              <UploadIcon />
              Importar
            </Link>
          </Button>

          <Button asChild>
            <Link to="create">
              <PlusIcon />
              Criar
            </Link>
          </Button>
        </div>
      </div>

      {queryEligibilityWatchlist.isLoading ||
      queryEligibilityWatchlist.isPending ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryEligibilityWatchlist.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          <DataTableToolbar
            table={table}
            actions={{
              route: 'eligibility-watchlist',
              useQuery: queryEligibilityWatchlist,
            }}
          >
            <Select
              value={selectColumnFilter}
              onValueChange={(e) => {
                setSelectColumnFilter(e);
              }}
            >
              <SelectTrigger className="w-auto">
                <FilterIcon className="mr-2 size-4" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                {[
                  { label: 'Nome', value: 'name' },
                  { label: 'Nº Identificação', value: 'id' },
                  { label: 'Loja', value: 'shop' },
                ].map((item, i) => (
                  <SelectItem key={i} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DebouncedInput
              placeholder={`Pesquisar...`}
              value={
                table.getColumn(selectColumnFilter)?.getFilterValue() ?? ''
              }
              onChange={(val) =>
                table.getColumn(selectColumnFilter)?.setFilterValue(val)
              }
              className="h-10 md:w-[255px]"
            />
          </DataTableToolbar>

          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {queryEligibilityWatchlist.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryEligibilityWatchlist.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : table.getState().columnFilters.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      Não foram encontrados resultados.
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Funcionários Internos / Lojas na base de dados'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DataTableFooter table={table} />
        </div>
      )}
    </>
  );
}
